/**
 * QuoteEditorModal.tsx
 *
 * The quote editor, porting legacy's Quote modal
 * (html/html/lib/patient-details/encounter-details-ds2.php).
 *
 * Shape follows RecordPatientPaymentModal: a full-size Modal, the action row in
 * the footer, collapsible sections in the body, one mutation per distinct
 * action, and refetch-on-success rather than optimistic patching.
 *
 * Totals are never computed here. The editor posts its working state to the
 * server and renders what comes back, so the figure on screen is the one Save
 * will store — legacy computed them separately in JS and SQL and they drifted
 * until you saved.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { getErrorMessage } from '@/services/apiClient';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import {
  cloneQuote,
  deleteQuote,
  getQuote,
  getQuoteFacilities,
  recalcQuote,
  saveQuote,
  unlockQuote,
  type CatalogFacility,
  type Quote,
  type QuoteDiscount,
  type QuoteEditorState,
  type QuoteFinancing,
  type QuoteItem,
  type QuoteProcedure,
  type QuoteTotals,
} from '@/services/quotes.service';
import { QuoteProceduresSection } from './sections/QuoteProceduresSection';
import { QuoteProductsSection } from './sections/QuoteProductsSection';
import { QuoteDiscountsSection } from './sections/QuoteDiscountsSection';
import { QuoteFinancingSection } from './sections/QuoteFinancingSection';
import { QuoteTotalsSection } from './sections/QuoteTotalsSection';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { PLANS_MODULE_KEY } from '@/pages/patientDetailSections';
import QuotePrintModal from './QuotePrintModal';
import { QuoteToolbarActions, type StagedQuotePlan } from './QuoteToolbarActions';

interface QuoteEditorModalProps {
  quoteId: number | null;
  patientName: string;
  onClose: () => void;
  onChanged: () => void;
  /** Open another quote in this editor — the copy Clone just made. */
  onOpenQuote: (quoteId: number) => void;
}

/** The editor's working copy, seeded from the loaded quote. */
interface Draft {
  /** The quote this copy was seeded from. Never sent; see `draft` below. */
  forQuoteId: number;
  facilityId: number | null;
  procedures: Partial<QuoteProcedure>[];
  items: Partial<QuoteItem>[];
  discounts: Partial<QuoteDiscount>[];
  financing: Partial<QuoteFinancing>[];
  anesthesiaLocked: boolean;
  operatingRoomLocked: boolean;
  costAnesthesia: number;
  costOr: number;
  activeQuote: boolean;
  locked: boolean;
  notes: string;
  /** The plan Find Plan picked for a quote with none; Save stores it. */
  stagedPlan: StagedQuotePlan | null;
}

function draftFrom(quote: Quote): Draft {
  return {
    forQuoteId: quote.quoteId,
    facilityId: quote.facilityId,
    procedures: quote.procedures ?? [],
    items: quote.items ?? [],
    discounts: quote.discounts ?? [],
    financing: quote.financing ?? [],
    anesthesiaLocked: quote.anesthesiaLocked,
    operatingRoomLocked: quote.operatingRoomLocked,
    costAnesthesia: quote.costAnesthesia,
    costOr: quote.costOr,
    activeQuote: quote.activeQuote,
    locked: quote.locked,
    notes: quote.notes ?? '',
    stagedPlan: null,
  };
}

function toEditorState(draft: Draft): QuoteEditorState {
  return {
    facilityId: draft.facilityId,
    procedures: draft.procedures,
    items: draft.items,
    discounts: draft.discounts,
    financing: draft.financing,
    anesthesiaLocked: draft.anesthesiaLocked,
    operatingRoomLocked: draft.operatingRoomLocked,
    costAnesthesia: draft.costAnesthesia,
    costOr: draft.costOr,
    activeQuote: draft.activeQuote,
    locked: draft.locked,
    notes: draft.notes,
    ...(draft.stagedPlan ? { planId: draft.stagedPlan.planId } : {}),
  };
}

export default function QuoteEditorModal({
  quoteId,
  patientName,
  onClose,
  onChanged,
  onOpenQuote,
}: QuoteEditorModalProps) {
  const queryClient = useQueryClient();
  const open = quoteId !== null;
  // isAdmin is hasMinUserLevel(3) — legacy's `$user_level < 3` money gate.
  const { isAdmin } = useAuth();
  const toast = useToast();
  // Legacy shows the "Plan # N" header and Find Plan only with the Plans
  // module on (encounter-details-ds2.php:427, :563).
  const { enabled: plansEnabled } = useModuleAccess(PLANS_MODULE_KEY);

  // The editor stays mounted while its host opens, closes and opens other
  // quotes, so the working copy and the totals each say which quote they
  // belong to, and are only used while that quote is the one open. Without
  // that, the first recalc of a newly opened quote posted the PREVIOUS quote's
  // working copy and the Totals panel ran one quote behind (SCRUM-2084).
  const [draftState, setDraft] = useState<Draft | null>(null);
  const draft = draftState && draftState.forQuoteId === quoteId ? draftState : null;
  const [totalsState, setTotalsState] = useState<{ quoteId: number; totals: QuoteTotals } | null>(null);
  const totals = totalsState && totalsState.quoteId === quoteId ? totalsState.totals : null;
  const [error, setError] = useState<string | null>(null);
  const [totalsStale, setTotalsStale] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClone, setConfirmClone] = useState(false);
  // What Print prints: the working copy as it stood when Print was pressed.
  const [printState, setPrintState] = useState<QuoteEditorState | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const { data: quote, isLoading, isError } = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: () => getQuote(quoteId as number),
    enabled: open,
  });

  const { data: facilities } = useQuery({
    queryKey: ['quote-facilities', quoteId],
    queryFn: () => getQuoteFacilities(quoteId as number),
    enabled: open,
    staleTime: 300_000,
  });

  // Seed the working copy once per quote load. Keyed on quoteId so reopening a
  // different quote reseeds, but an in-flight edit is never clobbered by a
  // background refetch of the same one.
  const seededFor = useRef<number | null>(null);
  useEffect(() => {
    if (quote && seededFor.current !== quote.quoteId) {
      seededFor.current = quote.quoteId;
      setDraft(draftFrom(quote));
      setTotalsState(null);
      setError(null);
    }
    if (!open) {
      seededFor.current = null;
      // Closing discards the working copy, so reopening the same quote starts
      // from what is stored rather than from edits the user walked away from.
      setDraft(null);
      setTotalsState(null);
    }
  }, [quote, open]);

  const locked = quote?.locked ?? false;
  const readOnly = locked;
  // Legacy shows Unlock only to user level > 2 (encounter-details-ds2.php:530-540).
  // The server makes the same check.
  const canUnlock = isAdmin;

  const facility = useMemo(
    () => (facilities ?? []).find((f: CatalogFacility) => f.facilityId === draft?.facilityId) ?? null,
    [facilities, draft?.facilityId],
  );

  /**
   * Legacy gates the money fields on user level, not just on the lock. The
   * procedure prices, the Anesthesia / OR figures, product Cost and Taxable,
   * and discount Amount, Type and Applies to render disabled for anyone below
   * level 3, and a facility may opt out of that with `nonAdminAllowEdit`
   * (quote-handling-ds.js:1424-1442, allowAdminEdit).
   *
   * The server applies the same rule to save, recalc and print
   * (QuotePricingGate), so these disabled controls match what it will accept.
   *
   * One deliberate divergence: legacy's override re-enables Normal Price but
   * not Effective Price, because the JS only re-enables the `.nonAdminEdit`
   * class the PHP puts on one of them. That is an inconsistency, not a rule, so
   * the same predicate covers both here.
   */
  const pricingReadOnly = readOnly || !(isAdmin || facility?.nonAdminAllowEdit === true);

  // ─── Totals ──────────────────────────────────────────────────────────────

  // Recalcs overlap: a fast later request can land before a slow earlier one.
  // Without a sequence check the older response would overwrite the newer
  // totals and quote the coordinator a price they have already edited away.
  const recalcSeq = useRef(0);
  // The quote open right now, for callbacks that outlive the render that
  // started them.
  const openQuoteRef = useRef(quoteId);
  useEffect(() => {
    openQuoteRef.current = quoteId;
  }, [quoteId]);

  const recalcMutation = useMutation({
    mutationFn: ({ forQuoteId, state }: { forQuoteId: number; state: QuoteEditorState; seq: number }) =>
      recalcQuote(forQuoteId, state),
    onSuccess: (result, variables) => {
      if (variables.seq !== recalcSeq.current) return;
      // Tagged with its quote, so an answer that outlives its quote is never
      // shown on the next one.
      setTotalsState({ quoteId: variables.forQuoteId, totals: result });
      setTotalsStale(false);
    },
    // Keep the last good figures on screen rather than blanking them, and say
    // plainly that they are behind.
    onError: (_error, variables) => {
      if (variables.seq !== recalcSeq.current) return;
      // A failure for a quote that is no longer open says nothing about the
      // figures on screen now.
      if (variables.forQuoteId !== openQuoteRef.current) return;
      setTotalsStale(true);
    },
  });

  // A working copy is always recalculated against the quote it came from.
  const runRecalc = useCallback(
    (next: Draft) => {
      recalcSeq.current += 1;
      recalcMutation.mutate({ forQuoteId: next.forQuoteId, state: toEditorState(next), seq: recalcSeq.current });
    },
    // recalcMutation is stable enough for this purpose; including it would
    // re-create the callback on every mutation state change and restart the
    // debounce below on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Debounced so typing a price does not fire a request per keystroke.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRecalc = useCallback(
    (next: Draft) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runRecalc(next), 400);
    },
    [runRecalc],
  );

  // When the open quote changes (or the editor closes), an edit still waiting
  // out its debounce belongs to the quote being left: drop it, and drop any
  // "figures are behind" notice that was about that quote too.
  useEffect(() => {
    setTotalsStale(false);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;
    };
  }, [quoteId]);

  // First totals as soon as this quote's working copy exists — once per quote,
  // not once per keystroke. Keying on `totals === null` alone re-fired
  // undebounced for every edit made before the first response came back.
  // `draft` is only ever the open quote's own copy, so the previous quote's is
  // never what gets sent.
  const primedFor = useRef<number | null>(null);
  useEffect(() => {
    if (draft && primedFor.current !== draft.forQuoteId) {
      primedFor.current = draft.forQuoteId;
      runRecalc(draft);
    }
    if (!open) {
      primedFor.current = null;
    }
  }, [draft, open, runRecalc]);

  const update = useCallback(
    (patch: Partial<Draft>) => {
      setDraft((current) => {
        if (!current) return current;
        const next = { ...current, ...patch };
        scheduleRecalc(next);
        return next;
      });
    },
    [scheduleRecalc],
  );

  // ─── Actions ─────────────────────────────────────────────────────────────

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
    onChanged();
  }, [queryClient, quoteId, onChanged]);

  // Save keeps the editor open and confirms, as legacy re-opens the quote with
  // "Quote details have been updated". The working copy is replaced with what
  // was stored, so rows Save just created carry their new ids and a second
  // Save updates them instead of adding them again.
  const saveMutation = useMutation({
    mutationFn: () => saveQuote(quoteId as number, toEditorState(draft as Draft)),
    onSuccess: (saved) => {
      setError(null);
      if (saved) {
        queryClient.setQueryData(['quote', quoteId], saved);
        seededFor.current = saved.quoteId;
        const next = draftFrom(saved);
        setDraft(next);
        runRecalc(next);
      }
      refresh();
      toast.success('Quote details have been updated.');
    },
    onError: (err) => setError(getErrorMessage(err, 'Could not save this quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  // Unlocking reseeds from the unlocked quote, so every control comes back
  // with the stored values rather than a draft taken while it was locked.
  const unlockMutation = useMutation({
    mutationFn: () => unlockQuote(quoteId as number),
    onSuccess: (unlocked) => {
      setError(null);
      if (unlocked) {
        queryClient.setQueryData(['quote', quoteId], unlocked);
        seededFor.current = unlocked.quoteId;
        const next = draftFrom(unlocked);
        setDraft(next);
        runRecalc(next);
      }
      refresh();
    },
    onError: (err) => setError(getErrorMessage(err, 'Could not unlock this quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  // Legacy's Clone is saveQuote2('new'): it copies what is on screen, confirms
  // with "Quote Cloned Successfully." and opens the copy
  // (quote-handling-ds.js:2994-3000, 3243-3245).
  const cloneMutation = useMutation({
    mutationFn: () => cloneQuote(quoteId as number, toEditorState(draft as Draft)),
    onSuccess: (copy) => {
      setError(null);
      onChanged();
      toast.success('Quote Cloned Successfully.');
      onOpenQuote(copy.quoteId);
    },
    onError: (err) => setError(getErrorMessage(err, 'Could not copy this quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  // Legacy asks "Create a copy of this quote?", or warns first when there are
  // unsaved changes, because they go on the copy and not on this quote.
  const unsaved = useMemo(
    () => !!quote && !!draft && JSON.stringify(toEditorState(draft)) !== JSON.stringify(toEditorState(draftFrom(quote))),
    [quote, draft],
  );

  const deleteMutation = useMutation({
    mutationFn: () => deleteQuote(quoteId as number),
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (err) => setError(getErrorMessage(err, 'Could not delete this quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  const busy =
    saveMutation.isPending || cloneMutation.isPending || deleteMutation.isPending || unlockMutation.isPending;

  // A discard prompt belongs to the quote it was raised over.
  useEffect(() => {
    setConfirmDiscard(false);
  }, [quoteId]);

  /**
   * The backdrop and the X come through here. With edits on screen they ask
   * first, because one stray tap on a phone sheet's backdrop would otherwise
   * throw away a long form.
   */
  function requestClose() {
    if (busy) return;
    if (unsaved) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  // No actions until the quote is actually loaded — a live Save or Delete over
  // a still-loading modal is a button that does the wrong thing.
  const footer = !draft ? null : (
    <>
      {/* Legacy hides every editing control on a locked quote and offers
          Unlock in their place, to managers only. */}
      {readOnly && canUnlock && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => unlockMutation.mutate()}
          loading={unlockMutation.isPending}
          disabled={busy}
        >
          Unlock
        </Button>
      )}
      {!readOnly && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setConfirmDelete(true)}
          // Legacy refuses to delete the active quote.
          disabled={busy || draft.activeQuote}
        >
          Delete
        </Button>
      )}
      <Button size="sm" variant="secondary" onClick={() => setPrintState(toEditorState(draft))} disabled={busy}>
        Print
      </Button>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setConfirmClone(true)}
        loading={cloneMutation.isPending}
        disabled={busy}
      >
        Clone
      </Button>
      {!readOnly && (
        <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={busy}>
          Save
        </Button>
      )}
    </>
  );

  return (
    <>
      <Modal
        isOpen={open}
        onClose={requestClose}
        title={patientName ? `Quote — ${patientName}` : 'Quote'}
        size="full"
        footer={footer}
        // Every open Modal listens for Escape on the document, so with this on a
        // single Escape over a stacked confirm or the print window would close
        // that dialog and the editor together. The X and the backdrop still close
        // it, through the unsaved-changes prompt.
        closeOnEscape={false}
      >
        {isLoading ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading quote…</div>
        ) : isError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {/*
              Fixed wording rather than getErrorMessage: a failed load carries
              no server message worth showing, and getErrorMessage passes a
              plain Error's own text straight through — which for a dropped
              connection is a raw string no coordinator can act on.
            */}
            Could not open this quote. Refresh the page, and if it keeps happening, contact
            Prospera support.
          </div>
        ) : draft ? (
          <div className="space-y-4">
            {error ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            {locked ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {canUnlock
                  ? 'This quote is locked, so it can’t be changed. Unlock it to make changes.'
                  : 'This quote is locked, so it can’t be changed. Ask a manager to unlock it.'}
              </div>
            ) : null}

            {/* Header row: the plan, facility and the two quote flags. */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                {/* Legacy's "Plan # N", opening the plan in a new tab
                    (encounter-details-ds2.php:418-441). */}
                {plansEnabled && quote && quote.planId > 0 ? (
                  <a
                    href={`/plans/${quote.planId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {`Plan # ${quote.planDisplayId ?? quote.planId}`}
                  </a>
                ) : null}
                {/* The label wraps the shared Select's trigger button, which is
                    what names it for a screen reader. */}
                <label className="flex items-center gap-2 text-sm text-slate-700 max-md:w-full">
                  <span className="font-medium">Facility</span>
                  {/* "No facility" takes a chosen facility off again, as
                      legacy's "Select One..." option did (value 0). It is sent
                      as 0, not null: the server reads a missing facility as
                      "keep the stored one". */}
                  <Select<number>
                    wrapperClassName="w-56 max-md:w-auto max-md:min-w-0 max-md:flex-1"
                    placeholder="Select one…"
                    options={[
                      { label: 'No facility', value: 0 },
                      ...(facilities ?? []).map((f: CatalogFacility) => ({ label: f.name, value: f.facilityId })),
                    ]}
                    value={draft.facilityId}
                    disabled={readOnly}
                    onChange={(facilityId) => update({ facilityId: facilityId ?? 0 })}
                  />
                </label>
              </div>

              <div className="flex items-center gap-4">
                <Checkbox
                  label="Active"
                  checked={draft.activeQuote}
                  disabled={readOnly}
                  onChange={(e) => update({ activeQuote: e.target.checked })}
                />
                {/* Legacy's Locked checkbox, open to anyone, stored on Save
                    (encounter-details-ds2.php:500-502). */}
                <Checkbox
                  label="Locked"
                  checked={draft.locked}
                  disabled={readOnly}
                  onChange={(e) => update({ locked: e.target.checked })}
                />
              </div>
            </div>

            <QuoteToolbarActions
              quoteId={quoteId as number}
              planId={quote?.planId ?? 0}
              readOnly={readOnly}
              plansEnabled={plansEnabled}
              stagedPlan={draft.stagedPlan}
              // Held in the working copy until Save. No recalc: recalc reads
              // the stored plan, so the pick changes nothing until Save.
              onStagePlan={(planId, displayId) =>
                setDraft((current) => (current ? { ...current, stagedPlan: { planId, displayId } } : current))
              }
              hasContent={draft.procedures.length + draft.items.length + draft.discounts.length > 0}
              onChanged={() => {
                // A preset rewrites the quote's contents, so reseed the
                // working copy from the server rather than merging — and
                // total the reseeded copy, which the once-per-quote prime
                // would otherwise skip, leaving the Totals panel blank.
                seededFor.current = null;
                primedFor.current = null;
                refresh();
              }}
            />

            <QuoteProceduresSection
              procedures={draft.procedures}
              readOnly={readOnly}
              pricingReadOnly={pricingReadOnly}
              facilityId={draft.facilityId}
              onChange={(procedures) => update({ procedures })}
            />

            <QuoteProductsSection
              items={draft.items}
              taxRate={totals?.taxRate ?? facility?.taxRate ?? 0}
              readOnly={readOnly}
              pricingReadOnly={pricingReadOnly}
              onChange={(items) => update({ items })}
            />

            <QuoteDiscountsSection
              discounts={draft.discounts}
              readOnly={readOnly}
              pricingReadOnly={pricingReadOnly}
              onChange={(discounts) => update({ discounts })}
            />

            <QuoteFinancingSection
              financing={draft.financing}
              quoteTotal={totals?.total ?? 0}
              readOnly={readOnly}
              onChange={(financing) => update({ financing })}
            />

            <QuoteTotalsSection
              totals={totals}
              stale={totalsStale}
              recalculating={recalcMutation.isPending}
              anesthesiaLocked={draft.anesthesiaLocked}
              operatingRoomLocked={draft.operatingRoomLocked}
              costAnesthesia={draft.costAnesthesia}
              costOr={draft.costOr}
              activeQuote={draft.activeQuote}
              readOnly={readOnly}
              pricingReadOnly={pricingReadOnly}
              onChange={(patch) => update(patch)}
              onRecalc={() => runRecalc(draft)}
            />
          </div>
        ) : null}
      </Modal>

      <QuotePrintModal
        quoteId={printState ? quoteId : null}
        workingState={printState ?? undefined}
        onClose={() => setPrintState(null)}
      />

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        title="Delete this quote?"
        message="The quote and everything on it will be removed. This can’t be undone."
        confirmLabel="Delete quote"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={confirmClone}
        onClose={() => setConfirmClone(false)}
        onConfirm={() => {
          setConfirmClone(false);
          cloneMutation.mutate();
        }}
        title="Copy this quote?"
        message={
          unsaved
            ? 'You have unsaved changes. They will go on the copy, and this quote will stay as it was last saved.'
            : 'Create a copy of this quote?'
        }
        confirmLabel="Create copy"
        variant="warning"
      />

      <ConfirmDialog
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        title="Discard your changes?"
        message="Your changes to this quote haven’t been saved. If you close it now, they will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        variant="warning"
      />
    </>
  );
}
